import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { cn } from '@/lib/utils';

/**
 * Unified error fallback used across the lesson page. Replaces the
 * earlier fragmented set of error UIs: MediaViewer's inline crash
 * card, PanelBoundary's "X panel failed" card, the artifact-level
 * FailureCard, and PageBoundary's body.
 *
 * Three variants - same visual shape, only scale changes - so a step
 * failure inside a sub-tab and a tab-level crash read as the same
 * design language:
 *
 *   - `step`   - compact card for inline artifact failures (text
 *                generation failed, mindmap failed, etc.)
 *   - `panel`  - medium card for panel-level crashes (Quiz panel
 *                failed, Sources panel failed)
 *   - `page`   - full-width centered for page-level errors
 *                (PageBoundary)
 *
 * Verb hierarchy (use consistently):
 *   - primary action → "Retry" (re-run the failed thing)
 *   - secondary action → "Reload page" (refresh the window)
 *
 * The page variant adds room for a tertiary "Go home" escape hatch.
 */

export type PanelFallbackVariant = 'step' | 'panel' | 'page';

export interface PanelFallbackAction {
  label: string;
  onClick: () => void;
  busy?: boolean;
}

export interface PanelFallbackProps {
  variant?: PanelFallbackVariant;
  title: string;
  description: string;
  /** Small footer line for context, e.g. "Failed 2m ago · text" */
  meta?: string;
  primary?: PanelFallbackAction;
  secondary?: PanelFallbackAction;
  /**
   * The title's heading level. `h3` suits a panel inside a sectioned page;
   * a page whose only heading above it is the `h1` passes `h2`, or axe's
   * `heading-order` fails on the skipped level.
   */
  titleAs?: 'h2' | 'h3';
  className?: string;
}

const VARIANT_PADDING: Record<PanelFallbackVariant, string> = {
  step: 'px-6 py-12',
  panel: 'px-6 py-12',
  page: 'px-6 py-16',
};

export function PanelFallback({
  variant = 'panel',
  title,
  description,
  meta,
  primary,
  secondary,
  titleAs: Title = 'h3',
  className,
}: PanelFallbackProps) {
  return (
    <div
      className={cn(
        'animate-fade-in-up flex h-full w-full flex-col items-center justify-center text-center',
        VARIANT_PADDING[variant],
        className
      )}
    >
      <div className='flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10'>
        <AlertCircle className='h-7 w-7 text-destructive-emphasis' />
      </div>
      <Title className='mt-4 text-base font-medium text-foreground'>
        {title}
      </Title>
      <p className='mt-1 max-w-sm text-sm text-muted-foreground'>
        {description}
      </p>
      {/* No `/70` opacity modifier. At 12px it measured 3.12:1 against the
          panel — under AA — and the line carries real information (when the
          step failed, and which step). The size and the muted token already
          give it the hierarchy the opacity was reaching for. */}
      {meta && <p className='mt-2 text-xs text-muted-foreground'>{meta}</p>}
      {(primary || secondary) && (
        <div className='mt-5 flex items-center gap-3 max-sm:[&>button]:h-11'>
          {primary && (
            <Button
              onClick={primary.onClick}
              disabled={primary.busy}
              className='min-w-[120px] gap-2'
            >
              {primary.busy && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
              {primary.label}
            </Button>
          )}
          {secondary && (
            <Button
              variant='outline'
              onClick={secondary.onClick}
              disabled={secondary.busy}
              className='min-w-[120px] gap-2 bg-muted/60 hover:bg-muted'
            >
              {secondary.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
